import React, { useState, useEffect } from 'react';
import { fetchAuth } from '../../utils/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const getStockStatus = (stock) => {
    if (stock <= 0) return { level: 'empty', label: 'Out of Stock' };
    if (stock <= 50) return { level: 'low', label: 'Low Stock' };
    return { level: 'healthy', label: 'In Stock' };
};

function PurchaseBillView({ user, handleApiError, showNotification }) {
    const [products, setProducts] = useState([]);
    const [purchaseItems, setPurchaseItems] = useState([]);
    const [supplierName, setSupplierName] = useState('');
    const [billNumber, setBillNumber] = useState('');
    const [scannedTotal, setScannedTotal] = useState(null);
    const [productSearch, setProductSearch] = useState('');
    const [isScanning, setIsScanning] = useState(false);

    // Product name autocomplete state
    const [newProductForm, setNewProductForm] = useState({ name: '', quantity: '', purchasePrice: '', salePrice: '' });
    const [autocompleteResults, setAutocompleteResults] = useState([]);
    const [showAutocomplete, setShowAutocomplete] = useState(false);

    // Supplier name autocomplete state
    const [pastSuppliers, setPastSuppliers] = useState([]);
    const [supplierSuggestions, setSupplierSuggestions] = useState([]);
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [prodRes, suppRes] = await Promise.all([
                    fetchAuth('/api/product-variants'),
                    fetchAuth('/api/purchases/suppliers'),
                ]);
                if (prodRes.ok) setProducts(await prodRes.json());
                if (suppRes.ok) setPastSuppliers(await suppRes.json());
            } catch (err) {
                handleApiError(err);
            }
        };
        fetchInitialData();
    }, [handleApiError]);

    const handleSupplierChange = (value) => {
        setSupplierName(value);
        if (value.trim().length > 0) {
            const matches = pastSuppliers.filter(s => s.toLowerCase().includes(value.toLowerCase()));
            setSupplierSuggestions(matches);
            setShowSupplierDropdown(matches.length > 0);
        } else {
            setShowSupplierDropdown(false);
        }
    };

    // Handles the new product form inputs + autocomplete for name field
    const handleNewProductChange = (e) => {
        const { name, value } = e.target;
        setNewProductForm(prev => ({ ...prev, [name]: value }));
        if (name === 'name' && value.trim().length > 0) {
            const matches = products.filter(p =>
                `${p.masterItem?.name || ''} ${p.variantName || ''}`.toLowerCase().includes(value.toLowerCase())
            );
            setAutocompleteResults(matches.slice(0, 8));
            setShowAutocomplete(matches.length > 0);
        } else if (name === 'name') {
            setShowAutocomplete(false);
        }
    };

    const handleAutocompleteSelect = (product) => {
        setNewProductForm(prev => ({
            ...prev,
            name: `${product.masterItem?.name || ''} (${product.variantName || ''})`,
            purchasePrice: product.purchasePrice || '',
            salePrice: product.salePrice || '',
        }));
        setShowAutocomplete(false);
    };

    // NEW FUNCTION: Adds a manually entered product to the bill
    const handleAddNewProduct = () => {
        if (!newProductForm.name || !newProductForm.quantity || !newProductForm.purchasePrice || !newProductForm.salePrice) {
            showNotification('Product name, quantity, purchase price, and sale price are required.', 'error');
            return;
        }

        const newItem = {
            productVariantId: null, // This is a new product
            masterItemId: null,
            name: newProductForm.name,
            quantity: parseFloat(newProductForm.quantity),
            purchasePrice: parseFloat(newProductForm.purchasePrice),
            salePrice: parseFloat(newProductForm.salePrice),
            nameAtPurchase: newProductForm.name,
        };

        setPurchaseItems(currentItems => [...currentItems, newItem]);
        setNewProductForm({ name: '', quantity: '', purchasePrice: '', salePrice: '' });
        showNotification('New product added to the bill.', 'success');
    };

    // NEW FUNCTION: Handles the file upload for the AI scan
    const handleScanBill = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsScanning(true);
        const formData = new FormData();
        formData.append('billImage', file);

        try {
            const res = await fetchAuth(`${API_URL}/api/purchases/scan`, {
                method: 'POST',
                body: formData,
                headers: { 'Content-Type': null }
            });
            const parsedData = await res.json();
            if (!res.ok) throw new Error(parsedData.message);

            setSupplierName(parsedData.supplierName || '');
            setBillNumber(parsedData.billNumber || '');

            // Set the explicitly extracted total if available
            if (parsedData.totalAmount) {
                setScannedTotal(parsedData.totalAmount);
            }

            // Map the parsed AI items to the purchaseItems state
            const aiItems = parsedData.items.map(item => ({
                productVariantId: null,
                masterItemId: null,
                name: item.name,
                quantity: item.quantity,
                purchasePrice: item.purchasePrice,
                salePrice: 0,
                nameAtPurchase: item.name
            }));
            setPurchaseItems(currentItems => [...currentItems, ...aiItems]);
            showNotification('Bill scanned successfully! Items have been added.', 'success');

        } catch (err) {
            showNotification(err.message || 'Failed to scan the bill. Please enter manually.', 'error');
        } finally {
            setIsScanning(false);
            e.target.value = null; // Reset the file input
        }
    };


    const addToPurchaseList = (product) => {
        setPurchaseItems(currentItems => {
            const existingItem = currentItems.find(item => item.productVariantId === product.id);
            if (existingItem) {
                return currentItems.map(item =>
                    item.productVariantId === product.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            } else {
                return [...currentItems, {
                    productVariantId: product.id,
                    masterItemId: product.masterItem?.id || null,
                    quantity: 1,
                    purchasePrice: 0,
                    salePrice: product.salePrice || 0,
                    nameAtPurchase: `${product.masterItem?.name || ''} (${product.variantName || ''})`,
                }];
            }
        });
    };

    const updateItemDetails = (index, field, value) => {
        setPurchaseItems(currentItems => {
            const newItems = [...currentItems];
            newItems[index] = { ...newItems[index], [field]: value };
            return newItems;
        });
    };

    // Use the mathematically calculated sum, unless the AI extracted a specific explicit total
    const calculatedSum = purchaseItems.reduce((total, item) => total + (item.purchasePrice * item.quantity), 0);
    const displayTotalAmount = scannedTotal !== null ? scannedTotal : calculatedSum;

    const handleFinalizePurchase = async () => {
        if (purchaseItems.length === 0 || !supplierName) {
            showNotification('Supplier details and items are required.', 'error');
            return;
        }

        const purchaseData = {
            supplierName,
            billNumber,
            items: purchaseItems,
            totalAmount: displayTotalAmount
        };

        try {
            const res = await fetchAuth('/api/purchases', {
                method: 'POST',
                body: JSON.stringify(purchaseData),
            });
            if (!res.ok) throw new Error((await res.json()).message);
            showNotification('Purchase bill recorded successfully!', 'success');
            // Reset form
            setPurchaseItems([]);
            setSupplierName('');
            setBillNumber('');
        } catch (err) {
            handleApiError(err);
        }
    };

    const filteredProducts = products.filter(p => `${p.masterItem?.name || ''} ${p.variantName || ''}`.toLowerCase().includes(productSearch.toLowerCase()));

    return (
        <div className="pos-view">
            <div className="pos-billing-panel">
                <div className="pos-billing-header">
                    <h3>New Purchase Bill</h3>
                    <div className="customer-input-group">
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Supplier Name..."
                                className="customer-input"
                                value={supplierName}
                                onChange={e => handleSupplierChange(e.target.value)}
                                onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 150)}
                                style={{ width: '100%' }}
                            />
                            {showSupplierDropdown && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                                    background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                                    borderRadius: '4px', maxHeight: '160px', overflowY: 'auto',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}>
                                    {supplierSuggestions.map((s, i) => (
                                        <div
                                            key={i}
                                            onMouseDown={() => { setSupplierName(s); setShowSupplierDropdown(false); }}
                                            style={{
                                                padding: '8px 12px', cursor: 'pointer',
                                                borderBottom: '1px solid var(--color-border)', fontSize: '13px'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-light)'}
                                            onMouseLeave={e => e.currentTarget.style.background = ''}
                                        >
                                            {s}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input
                            type="text"
                            placeholder="Bill Number (Optional)..."
                            className="customer-input"
                            value={billNumber}
                            onChange={e => setBillNumber(e.target.value)}
                        />
                        <div style={{ marginTop: '16px', textAlign: 'center' }}>
                            <label htmlFor="bill-upload" className="btn-secondary" style={{ cursor: 'pointer' }}>
                                {isScanning ? 'Scanning...' : 'Scan Purchase Bill with AI'}
                            </label>
                            <input
                                id="bill-upload"
                                type="file"
                                onChange={handleScanBill}
                                style={{ display: 'none' }}
                                accept="image/*, application/pdf"
                                disabled={isScanning}
                            />
                        </div>
                    </div>
                </div>
                <div className="pos-cart-items">
                    {purchaseItems.length === 0 ? <p className="empty-cart-message">Add products to this bill</p> : (
                        <table className="cart-table">
                            <thead><tr><th>Item Name</th><th>Qty</th><th>Purchase Price</th><th>Sale Price</th><th>Profit %</th></tr></thead>
                            <tbody>{purchaseItems.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <input
                                            type="text"
                                            value={item.nameAtPurchase || ''}
                                            onChange={e => updateItemDetails(index, 'nameAtPurchase', e.target.value)}
                                            style={{ width: '150px' }}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.quantity}
                                            onChange={e => updateItemDetails(index, 'quantity', parseFloat(e.target.value) || 0)}
                                            style={{ width: '60px' }}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={item.purchasePrice || ''}
                                            onChange={e => updateItemDetails(index, 'purchasePrice', parseFloat(e.target.value))}
                                            style={{ width: '90px' }}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={item.salePrice || ''}
                                            onChange={e => updateItemDetails(index, 'salePrice', parseFloat(e.target.value))}
                                            style={{ width: '90px' }}
                                        />
                                    </td>
                                    <td>
                                        {item.purchasePrice > 0 && item.salePrice > 0 ?
                                            `${(((item.salePrice - item.purchasePrice) / item.purchasePrice) * 100).toFixed(1)}%` :
                                            '-'
                                        }
                                    </td>
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
                </div>
                <div className="pos-billing-footer">
                    <div className="total-section"><h4>Total Amount</h4><span>₹{displayTotalAmount.toFixed(2)}</span></div>
                    <button className="finalize-btn" onClick={handleFinalizePurchase} style={{ backgroundColor: 'var(--color-primary)' }}>Finalize Purchase</button>
                </div>
            </div>
            <div className="pos-product-panel">
                <input type="text" placeholder="Search products..." className="product-search-input" value={productSearch} onChange={e => setProductSearch(e.target.value)} />
                <div className="product-grid">
                    {filteredProducts.map(p => {
                        const status = getStockStatus(p.masterItem?.stock || 0);
                        return (
                            <div key={p.id} className={`product-card stock-card-accent--${status.level}`} onClick={() => addToPurchaseList(p)}>
                                <p className="product-name">{p.masterItem?.name} ({p.variantName})</p>
                                <span className={`stock-badge stock-badge--${status.level}`} style={{fontSize: '0.7rem', padding: '2px 8px'}}>
                                    <span className={`stock-dot stock-dot--${status.level}`}></span>
                                    {p.masterItem?.stock || 0} — {status.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
                <hr style={{ margin: '16px 0', borderColor: 'var(--color-border)' }} />
                <div className="add-new-product-box">
                    <h4>Add New Product to Stock</h4>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            name="name"
                            placeholder="Product Name (search existing or type new)"
                            value={newProductForm.name}
                            onChange={handleNewProductChange}
                            onBlur={() => setTimeout(() => setShowAutocomplete(false), 150)}
                            style={{ width: '100%' }}
                        />
                        {showAutocomplete && (
                            <div style={{
                                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                                borderRadius: '4px', maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }}>
                                {autocompleteResults.map(p => (
                                    <div
                                        key={p.id || p._id}
                                        onMouseDown={() => handleAutocompleteSelect(p)}
                                        style={{
                                            padding: '8px 12px', cursor: 'pointer',
                                            borderBottom: '1px solid var(--color-border)',
                                            fontSize: '13px'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-light)'}
                                        onMouseLeave={e => e.currentTarget.style.background = ''}
                                    >
                                        <strong>{p.masterItem?.name}</strong> ({p.variantName}) &mdash; <span className={`stock-badge stock-badge--${getStockStatus(p.masterItem?.stock || 0).level}`} style={{fontSize:'0.7rem',padding:'2px 6px'}}>Stock: {p.masterItem?.stock}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <input type="number" name="quantity" placeholder="Quantity" value={newProductForm.quantity} onChange={handleNewProductChange} />
                    <input type="number" name="purchasePrice" placeholder="Purchase Price" value={newProductForm.purchasePrice} onChange={handleNewProductChange} />
                    <input type="number" name="salePrice" placeholder="Sale Price" value={newProductForm.salePrice} onChange={handleNewProductChange} />
                    {newProductForm.purchasePrice && newProductForm.salePrice && parseFloat(newProductForm.purchasePrice) > 0 && (
                        <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--color-success)' }}>
                            Profit Margin: {(((parseFloat(newProductForm.salePrice) - parseFloat(newProductForm.purchasePrice)) / parseFloat(newProductForm.purchasePrice)) * 100).toFixed(2)}%
                        </p>
                    )}
                    <button onClick={handleAddNewProduct} className="btn-primary" style={{ width: '100%', marginTop: '8px' }}>Add Product to Bill & Stock</button>
                </div>
            </div>
        </div>
    );
}

export default PurchaseBillView;
